import { type FC } from "react";
import { Controller, type Control } from "react-hook-form";
import styled from "styled-components";

import { isContentTypeAudio, isContentTypeImage, isContentTypeVideo } from "@ds/modules/assets/utils/helpers";
import { assetMediaTypes } from "@ds/modules/assets/utils/constants";

import { useDetailsInlineSaving } from "@ds/components/details-view/hooks";
import { Checkbox, type CheckboxChangeEvent } from "@ds/components/forms/inputs";
import { SpanWrapper } from "@ds/components/forms/labels/span-wrapper";
import { useOnMouseEventPreventDefaultHandler } from "@ds/utils/handlers";

const CheckboxGroupWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  margin-top: 12px;
`;

const WrappedCheckbox = styled(Checkbox)`
  margin-right: 1rem;
`;

export const ASSET_MEDIA_TYPE_CHECKBOX_LABEL = "asset-media-type-checkbox";

const mediaCategories = [
  { label: "Video", types: Object.keys(assetMediaTypes.video), isType: isContentTypeVideo },
  { label: "Image", types: Object.keys(assetMediaTypes.image), isType: isContentTypeImage },
  { label: "Audio", types: Object.keys(assetMediaTypes.audio), isType: isContentTypeAudio },
] as const;

export const PlaylistMediaTypesControl: FC<{
  control: Control<Playlist>;
  autoFocus?: boolean;
  excludeLabel?: boolean;
  onBlur?: () => void;
}> = ({ control, autoFocus, excludeLabel, onBlur }) => {
  const onBlurHandler = useDetailsInlineSaving(onBlur);
  const onMouseDownHandler = useOnMouseEventPreventDefaultHandler();

  return (
    <Controller
      name="media_types"
      control={control as Control<Playlist>}
      render={({ field: { value, onChange, onBlur: onBlurFn }, fieldState }) => {
        const element = (
          <CheckboxGroupWrapper onBlur={onBlurHandler(onBlurFn)} onMouseDown={onMouseDownHandler}>
            {mediaCategories.map((category, index) => {
              const isChecked = value.some(category.isType);
              return (
                <CheckboxWrapper key={category.label}>
                  <WrappedCheckbox
                    aria-label={ASSET_MEDIA_TYPE_CHECKBOX_LABEL}
                    value={category.label}
                    checked={isChecked}
                    autoFocus={autoFocus && index === 0}
                    onChange={(e: CheckboxChangeEvent) => {
                      if (e.checked) {
                        const existing = value.filter(mt => !category.isType(mt));
                        onChange([...existing, ...category.types]);
                      } else {
                        onChange(value.filter(mt => !category.isType(mt)));
                      }
                    }}
                  />
                  <span className="font-medium">{category.label}</span>
                </CheckboxWrapper>
              );
            })}
          </CheckboxGroupWrapper>
        );

        if (excludeLabel) {
          return element;
        }

        return (
          <SpanWrapper label="Media Types" className="col-8" error={fieldState.error} isRequired>
            {element}
          </SpanWrapper>
        );
      }}
    />
  );
};
